import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { createCustomAdventureBookingRequest } from '@/utils/customAdventureApi';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Separator } from "@/components/ui/separator";
import { ShieldCheck } from 'lucide-react';
import { formatCurrency } from '@/utils/adventureUtils';
import { 
  type BookingRequest,
  createApiBookingRequest,
  sessionStorageUtils
} from '@/components/public/adventures/utils/customAdventureUtils';

interface UseFormSubmissionProps {
  selectedAdventure: CustomAdventure | null;
  setBookingData: React.Dispatch<React.SetStateAction<any>>;
}

export const useFormSubmission = ({ selectedAdventure, setBookingData }: UseFormSubmissionProps) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = useCallback(async (bookingRequest: BookingRequest) => {
    if (!selectedAdventure || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Show immediate feedback with progress
    toast.loading(t('booking.submitting.title'), {
      description: t('booking.submitting.description'),
      id: 'booking-progress'
    });
    
    try {
      // Create the API request using utility function
      const apiRequest = createApiBookingRequest(bookingRequest, selectedAdventure);

      // Submit the booking request
      const response = await createCustomAdventureBookingRequest(apiRequest);
      
      // Dismiss loading toast
      toast.dismiss('booking-progress');

      // Store request number for future reference
      sessionStorageUtils.saveBookingRequest(response.requestNumber);

      // Prepare confirmation page data
      const confirmationData = {
        requestNumber: response.requestNumber,
        adventureName: selectedAdventure.name,
        businessName: 'Adventure Provider', // Default business name - will be populated from backend data
        dateRange: {
          startDate: bookingRequest.dateRange.from!.toISOString(),
          endDate: bookingRequest.dateRange.to!.toISOString(),
          flexibility: bookingRequest.flexibility
        },
        participants: {
          adults: Number(bookingRequest.selectedOptions['Number of Adults'] || 
                  bookingRequest.selectedOptions['Number of People'] || 
                  bookingRequest.selectedOptions['Adults'] || 1),
          children: Number(bookingRequest.selectedOptions['Number of Children'] || 
                    bookingRequest.selectedOptions['Children'] || 0),
          students: Number(bookingRequest.selectedOptions['Number of Students'] || 
                    bookingRequest.selectedOptions['Students'] || 0)
        },
        estimatedPrice: {
          totalEstimate: response.estimatedPrice.totalEstimate,
          currency: response.estimatedPrice.currency
        },
        priceBreakdown: (response as any).priceBreakdown ?? bookingRequest.priceBreakdown,
        customerDetails: bookingRequest.customerDetails,
        selectedOptions: bookingRequest.selectedOptions,
        status: response.status,
        createdAt: new Date().toISOString()
      };

      // Navigate to request confirmation page with data
      navigate(`/custom-adventure-request-confirmation/${response.requestNumber}?data=${encodeURIComponent(JSON.stringify(confirmationData))}`);

    } catch (error) {
      console.error('Error submitting booking request:', error);
      
      // Dismiss loading toast
      toast.dismiss('booking-progress');
      
      // Check if it's an email sending error
      const isEmailError = error instanceof Error && 
        (error.message.includes('email') || error.message.includes('Connection timeout'));
      
      // Show appropriate error message
      toast.error(
        isEmailError ? t('booking.email.error.title') : t('booking.error.title'),
        {
          description: isEmailError 
            ? t('booking.email.error.description')
            : (error instanceof Error ? error.message : t('booking.error.description')),
          duration: 7000, // Give more time to read email-specific errors
          action: isEmailError ? undefined : {
            label: t('booking.error.action'),
            onClick: () => handleFormSubmit(bookingRequest)
          }
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedAdventure, t, setBookingData, navigate]);

  return {
    handleFormSubmit,
    isSubmitting
  };
};
